import { app } from '@awork/environments/environment'

export interface SelectOptions<Entity> {
  filterBy?: FilterBy<Entity>
  limitTo?: number
  sortBy?: SortBy<Entity>
  sortByOrder?: Order
}

type FilterBy<Entity> =
  ((entity: Entity, index?: number) => boolean) | ((entity: Entity, index?: number) => boolean)[] | undefined

type SortBy<Entity, State = unknown> = ((a: Entity, b: Entity, state?: State) => number) | keyof Entity

export enum Order {
  ASC = 'asc',
  DESC = 'desc'
}

export interface GenericEntity {
  id: string
  resourceVersion?: number
}

export type EntityProps<Entity> = Entity & GenericEntity extends Entity ? keyof Entity : never

export interface Paging {
  page: number
  pageSize: number
}

export interface SyncOptions<Entity> {
  compareProp?: EntityProps<Entity> // Property used to do the update comparison
  keepProp?: EntityProps<Entity> // Property to keep in store when updating the entity
  resetFunction?: Function // Function to reset certain entity properties in the store
  paging?: Paging // Paging information used when fetching certain page from the API
  nullableProps?: EntityProps<Entity>[] // Needed to remove the value from the store when the API doesn't return it
}

export const DEFAULT_PERSIST_DELAY = app === 'web' ? 5000 : 2000

export interface SyncResponse<Entity> {
  updated: Entity[]
  unfilteredUpdated?: string[] // Entities that don't pass the filter anymore after an update
  deleted: string[]
  inaccessible?: string[]
  reconciliationRequired?: boolean
  lastSync: Date | null
}

export interface SyncFilter {
  filterExpression: string
  entityIds: string[]
  lastSync: Date | null
}

/**
 * Emitted when entities are removed from the store while a sync filter still tracks them.
 */
export interface TrackedEntitiesRemovedEvent {
  /** Action name of the removal, identifies the calling code path */
  action: string
  /** Removed entity ids that are tracked by at least one sync filter */
  entityIds: string[]
  syncFilters: { filterExpression: string; removedCount: number; trackedCount: number }[]
}

export const SYNC_FILTERS_CLEANUP_THRESHOLD = 30 // 30 days
